import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { HiEnvelope, HiBriefcase, HiBuildingOffice2 } from 'react-icons/hi2'
import { cnMerge } from '../../utils/twmerge'
import type { User } from '../../models'

interface UserPopupProps {
  user: User
  children: React.ReactNode
  trigger?: 'hover' | 'click' | 'both'
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto'
  className?: string
  disabled?: boolean
  showActions?: boolean
  onContact?: ((user: User) => void) | undefined
  onViewProfile?: ((user: User) => void) | undefined
}

export const UserPopup: React.FC<UserPopupProps> = ({
  user,
  children,
  trigger = 'both',
  position = 'auto',
  className,
  disabled = false,
  showActions = true,
  onContact,
  onViewProfile
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [popupCoords, setPopupCoords] = useState({ top: 0, left: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Calcular posição automática do popup
  useEffect(() => {
    if (isVisible && containerRef.current) {
      const container = containerRef.current.getBoundingClientRect()
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }
      
      // Determinar melhor posição baseado no espaço disponível
      const spaceRight = viewport.width - container.right
      const spaceLeft = container.left
      const spaceBottom = viewport.height - container.bottom
      const spaceTop = container.top

      let finalPosition = position
      let coords = { top: 0, left: 0 }

      if (position === 'auto') {
        if (spaceBottom >= 200) {
          finalPosition = 'bottom'
        } else if (spaceTop >= 200) {
          finalPosition = 'top'
        } else if (spaceRight >= 300) {
          finalPosition = 'right'
        } else if (spaceLeft >= 300) {
          finalPosition = 'left'
        } else {
          finalPosition = 'bottom'
        }
      } else {
        finalPosition = position
      }

      // Calcular coordenadas absolutas baseadas na posição
      const popupWidth = 320 // min-w-72 = 288px + padding
      const popupHeight = 200 // estimativa

      switch (finalPosition) {
        case 'top':
          coords = {
            top: container.top - popupHeight - 8,
            left: container.left + (container.width / 2) - (popupWidth / 2)
          }
          break
        case 'bottom':
          coords = {
            top: container.bottom + 8,
            left: container.left + (container.width / 2) - (popupWidth / 2)
          }
          break
        case 'left':
          coords = {
            top: container.top + (container.height / 2) - (popupHeight / 2),
            left: container.left - popupWidth - 8
          }
          break
        case 'right':
          coords = {
            top: container.top + (container.height / 2) - (popupHeight / 2),
            left: container.right + 8
          }
          break
        default:
          coords = {
            top: container.bottom + 8,
            left: container.left + (container.width / 2) - (popupWidth / 2)
          }
      }

      // Ajustar para não sair da tela
      coords.left = Math.max(8, Math.min(coords.left, viewport.width - popupWidth - 8))
      coords.top = Math.max(8, Math.min(coords.top, viewport.height - popupHeight - 8))

      setPopupCoords(coords)
    }
  }, [isVisible, position])

  const showPopup = () => {
    if (disabled) return
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsVisible(true)
  }

  const hidePopup = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 150)
  }

  const cancelHide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  // Handlers baseados no trigger
  const handlers = {
    onMouseEnter: (trigger === 'hover' || trigger === 'both') ? showPopup : undefined,
    onMouseLeave: (trigger === 'hover' || trigger === 'both') ? hidePopup : undefined,
    onClick: (trigger === 'click' || trigger === 'both') ? (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isVisible) {
        setIsVisible(false)
      } else {
        showPopup()
      }
    } : undefined
  }

  // Cleanup do timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Fechar popup ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(event.target as Node) &&
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false)
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    
    return () => {} // Return vazio quando não há listener
  }, [isVisible])


  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <>
      <div 
        ref={containerRef}
        className={cnMerge("relative inline-block", className)}
        {...handlers}
      >
        {children}
      </div>
      
      {/* Popup usando Portal */}
      {isVisible && createPortal(
        <div
          ref={popupRef}
          data-user-popup
          className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 min-w-72 max-w-80"
          style={{
            top: `${popupCoords.top}px`,
            left: `${popupCoords.left}px`
          }}
          onMouseEnter={cancelHide}
          onMouseLeave={hidePopup}
        >
          {/* Header com avatar e nome */}
          <div className="flex items-center space-x-3 mb-3">
            <div className="flex-shrink-0">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500 text-white font-medium">
                  {getInitials(user.name)}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {user.name}
              </h4>
              {user.funcao_usuario && (
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  {user.funcao_usuario}
                </p>
              )}
            </div>
          </div>

          {/* Informações detalhadas */}
          <div className="space-y-2 mb-3">
            {user.email && (
              <div className="flex items-center space-x-2">
                <HiEnvelope className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{user.email}</span>
              </div>
            )}
            
            {user.cargo_usuario && (
              <div className="flex items-center space-x-2">
                <HiBriefcase className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{user.cargo_usuario}</span>
              </div>
            )}
            
            {user.setor?.nome_setor && (
              <div className="flex items-center space-x-2">
                <HiBuildingOffice2 className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{user.setor.nome_setor}</span>
              </div>
            )}
          </div>

          {/* Ações (se habilitadas) */}
          {showActions && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3 flex space-x-2">
              {onContact && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onContact(user)
                    setIsVisible(false)
                  }}
                  className="flex-1 text-xs px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                >
                  Contatar
                </button>
              )}
              
              {onViewProfile && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onViewProfile(user)
                    setIsVisible(false)
                  }}
                  className="flex-1 text-xs px-3 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Ver Perfil
                </button>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}
